import { Link } from 'react-router-dom'
import { SEO } from '@/components/SEO'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useLuxeGrid } from '@/hooks'
import { ArrowRight, Calendar, BookOpen } from 'lucide-react'
import { posts } from './blog-posts'

export function Blog() {
  // Hero entrance
  const heroRef = useLuxeHero<HTMLElement>(0.3)

  // Posts: wave stagger
  const postsRef = useLuxeGrid<HTMLElement>('.post-card', {
    stagger: 0.15,
    blur: 6,
  })

  // Sort posts by date, newest first
  const sortedPosts = [...posts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  return (
    <>
      <SEO
        title="Blog"
        description="Thoughts on software engineering, AI infrastructure, AWS, and building things that matter."
        url="https://software.garrettjohnlaw.com/blog"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative overflow-hidden px-4 py-20 md:py-28">
        <div className="bg-gradient-radial-cyber absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <div className="mb-4 flex items-center justify-center gap-3">
            <BookOpen className="text-primary h-8 w-8" />
            <p className="text-primary font-mono text-lg font-medium tracking-wide">&lt;blog /&gt;</p>
          </div>
          <h1 className="text-fluid-h1 text-foreground mb-6">Blog</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Thoughts on software, systems, strategy, and building things that matter.
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Posts Section */}
      <section ref={postsRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          {sortedPosts.length === 0 ? (
            <GlassCard accent className="py-12 text-center">
              <BookOpen className="text-primary/50 mx-auto mb-4 h-12 w-12" />
              <p className="text-muted-foreground">No posts yet. Check back soon!</p>
            </GlassCard>
          ) : (
            <div className="space-y-8">
              {sortedPosts.map((post) => (
                <article key={post.slug} className="post-card">
                  <Link to={`/blog/${post.slug}`} className="group block">
                    <GlassCard hover className="overflow-hidden p-0">
                      {post.coverImage && (
                        <div className="relative aspect-[16/9] w-full overflow-hidden">
                          <img
                            src={post.coverImage}
                            alt={post.title}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="from-background via-background/20 absolute inset-0 bg-gradient-to-t to-transparent" />
                        </div>
                      )}
                      <div className="space-y-3 p-6 md:p-8">
                        <div className="text-primary flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </div>
                        <h2 className="text-foreground group-hover:text-primary text-xl font-semibold transition-colors md:text-2xl">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground">{post.description}</p>
                        <div className="text-primary flex items-center gap-2 pt-2 font-medium">
                          Read more
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </GlassCard>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  )
}
